import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProjects, createProject, updateProject, deleteProject } from '../utils/api';

const ProjectsPage = () => {
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    industry_vertical: '',
    module_details: '',
    implementation_scope: ''
  });
  const [sowDocument, setSowDocument] = useState(null);
  const [rfpDocument, setRfpDocument] = useState(null);
  const [otherDocuments, setOtherDocuments] = useState([]);

  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await getProjects();
        if (response.success) {
          setProjects(response.projects || []);
        } else {
          console.error('Error fetching projects:', response.message);
          // Show fallback data during development
          setProjects([
            {
              id: 1,
              name: 'SAP S/4HANA Implementation (Demo)',
              description: 'End-to-end implementation of SAP S/4HANA for manufacturing',
              industry_vertical: 'Manufacturing',
              module_details: 'Finance, MM, SD, PP',
              implementation_scope: 'Full implementation',
              created_at: '2025-04-20T10:30:00Z',
              updated_at: '2025-04-22T14:45:00Z'
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleOpenDialog = (project = null) => {
    if (project) {
      setSelectedProject(project);
      setFormData({
        name: project.name,
        description: project.description || '',
        industry_vertical: project.industry_vertical || '',
        module_details: project.module_details || '',
        implementation_scope: project.implementation_scope || ''
      });
    } else {
      setSelectedProject(null);
      setFormData({
        name: '',
        description: '',
        industry_vertical: '',
        module_details: '',
        implementation_scope: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProject(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSowDocumentChange = (e) => {
    setSowDocument(e.target.files[0]);
  };

  const handleRfpDocumentChange = (e) => {
    setRfpDocument(e.target.files[0]);
  };

  const handleOtherDocumentsChange = (e) => {
    setOtherDocuments(Array.from(e.target.files));
  };

  const handleSubmit = async () => {
    try {
      let response;
      
      if (selectedProject) {
        // Update existing project
        response = await updateProject(selectedProject.id, formData);
        if (response.success) {
          // Update the local state with the updated project
          setProjects(projects.map(p => 
            p.id === selectedProject.id ? response.project : p
          ));
        } else {
          alert(response.message || 'Failed to update project');
          return;
        }
      } else {
        // Create new project with files
        const projectFormData = new FormData();
        
        // Add project details
        Object.keys(formData).forEach(key => {
          projectFormData.append(key, formData[key]);
        });
        
        // Add SOW document if selected
        if (sowDocument) {
          projectFormData.append('sow_document', sowDocument);
        }
        
        // Add RFP document if selected
        if (rfpDocument) {
          projectFormData.append('rfp_document', rfpDocument);
        }
        
        // Add other documents if selected
        if (otherDocuments.length > 0) {
          otherDocuments.forEach(doc => {
            projectFormData.append('other_documents', doc);
          });
        }
        
        response = await createProject(projectFormData);
        if (response.success) {
          // Add the new project to the local state
          setProjects([...projects, response.project]);
        } else {
          alert(response.message || 'Failed to create project');
          return;
        }
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('An unexpected error occurred. Please try again.');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        const response = await deleteProject(projectId);
        if (response.success) {
          // Remove the deleted project from the local state
          setProjects(projects.filter(p => p.id !== projectId));
        } else {
          alert(response.message || 'Failed to delete project');
        }
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('An unexpected error occurred while deleting the project.');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Projects
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          New Project
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Industry</TableCell>
                <TableCell>Modules</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.length > 0 ? (
                projects.map((project) => (
                  <TableRow key={project.id}>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {project.name}
                      </Typography>
                      {project.description && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                          {project.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {project.industry_vertical || '-'}
                    </TableCell>
                    <TableCell>
                      {project.module_details?.split(',').map((module, index) => (
                        <Chip 
                          key={index}
                          label={module.trim()}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      {formatDate(project.updated_at)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="View Details">
                          <Button
                            variant="outlined"
                            size="small"
                            component={RouterLink}
                            to={`/projects/${project.id}`}
                            sx={{ mr: 1 }}
                          >
                            View Details
                          </Button>
                        </Tooltip>
                        <Tooltip title="Generate Spec">
                          <Button
                            variant="outlined"
                            size="small"
                            component={RouterLink}
                            to={`/generate?project=${project.id}`}
                            sx={{ mr: 1 }}
                          >
                            Generate
                          </Button>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(project)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="textSecondary" py={2}>
                      No projects found. Create your first project!
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Project Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedProject ? 'Edit Project' : 'Create New Project'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            name="name"
            label="Project Name"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            name="description"
            label="Description"
            value={formData.description}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            name="industry_vertical"
            label="Industry Vertical"
            value={formData.industry_vertical}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            name="module_details"
            label="SAP Modules"
            value={formData.module_details}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            helperText="Comma-separated list of SAP modules (e.g., 'FI, MM, SD')"
          />
          <TextField
            name="implementation_scope"
            label="Implementation Scope"
            value={formData.implementation_scope}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            select
            SelectProps={{
              native: true
            }}
          >
            <option value=""></option>
            <option value="Full implementation">Full implementation</option>
            <option value="Enhancement">Enhancement</option>
            <option value="Migration">Migration</option>
            <option value="Integration">Integration</option>
            <option value="Support">Support</option>
          </TextField>
          
          {!selectedProject && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Upload Documents
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Project Documents
                </Typography>
                <input
                  accept=".pdf,.docx,.doc,.txt,.md"
                  id="sow-document-upload"
                  type="file"
                  onChange={handleSowDocumentChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="sow-document-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    {sowDocument ? sowDocument.name : 'Upload Project Documents'}
                  </Button>
                </label>
                {sowDocument && (
                  <Typography variant="caption" display="block" gutterBottom>
                    Selected: {sowDocument.name} ({Math.round(sowDocument.size/1024)} KB)
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Request for Proposal (RFP) - Optional
                </Typography>
                <input
                  accept=".pdf,.docx,.doc,.txt,.md"
                  id="rfp-document-upload"
                  type="file"
                  onChange={handleRfpDocumentChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="rfp-document-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    {rfpDocument ? rfpDocument.name : 'Upload RFP Document'}
                  </Button>
                </label>
                {rfpDocument && (
                  <Typography variant="caption" display="block" gutterBottom>
                    Selected: {rfpDocument.name} ({Math.round(rfpDocument.size/1024)} KB)
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Additional Documents (Optional)
                </Typography>
                <input
                  accept=".pdf,.docx,.doc,.txt,.md"
                  id="other-documents-upload"
                  type="file"
                  multiple
                  onChange={handleOtherDocumentsChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="other-documents-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                  >
                    Upload Additional Documents
                  </Button>
                </label>
                {otherDocuments.length > 0 && (
                  <Typography variant="caption" display="block" gutterBottom>
                    {otherDocuments.length} file(s) selected
                  </Typography>
                )}
                {otherDocuments.length > 0 && (
                  <Box sx={{ mt: 1, maxHeight: '100px', overflowY: 'auto' }}>
                    {otherDocuments.map((doc, index) => (
                      <Typography key={index} variant="caption" display="block">
                        {doc.name} ({Math.round(doc.size/1024)} KB)
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectsPage;